import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/NotFound.css";

export default function NotFound() {
  const navigate = useNavigate();
  const eyeLeftRef = useRef(null);
  const eyeRightRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const move = (eyeRef, offsetX) => {
        if (!eyeRef.current) return;
        const rect = eyeRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const angle = Math.atan2(dy, dx);
        const dist = Math.min(Math.hypot(dx, dy), 4);
        const px = Math.cos(angle) * dist;
        const py = Math.sin(angle) * dist;
        eyeRef.current.style.transform = `translate(${px}px, ${py}px)`;
      };
      move(eyeLeftRef, -10);
      move(eyeRightRef, 10);
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="nf-root">
      {/* ── LEAVES BACKGROUND ── */}
      <div className="nf-leaves" aria-hidden="true">
        {[...Array(14)].map((_, i) => (
          <div key={i} className={`nf-leaf nf-leaf--${i + 1}`} />
        ))}
      </div>

      {/* ── FIREFLIES ── */}
      <div className="nf-fireflies" aria-hidden="true">
        {[...Array(18)].map((_, i) => (
          <div key={i} className={`nf-firefly nf-firefly--${i + 1}`} />
        ))}
      </div>

      {/* ── CONTENT ── */}
      <div className="nf-content">
        {/* CHAMELEON SVG */}
        <div className="nf-chameleon-wrap" aria-hidden="true">
          <svg
            viewBox="0 0 420 260"
            xmlns="http://www.w3.org/2000/svg"
            className="nf-chameleon-svg"
          >
            {/* Branch */}
            <rect x="20" y="200" width="380" height="14" rx="7" fill="#1a2e18" />
            <rect x="60" y="192" width="300" height="10" rx="5" fill="#243d20" />

            {/* Tail coil */}
            <path
              d="M 80 200 Q 55 195 45 210 Q 35 225 50 232 Q 65 238 70 225 Q 72 215 60 212"
              fill="none" stroke="#4a7a35" strokeWidth="9" strokeLinecap="round"
            />
            <path
              d="M 80 200 Q 55 195 45 210 Q 35 225 50 232 Q 65 238 70 225 Q 72 215 60 212"
              fill="none" stroke="#5c9440" strokeWidth="5" strokeLinecap="round"
            />

            {/* Body */}
            <ellipse cx="190" cy="188" rx="95" ry="38" fill="#3d6e2a" />
            <ellipse cx="190" cy="184" rx="90" ry="32" fill="#4d8a35" />

            {/* Body pattern diamonds */}
            {[150, 185, 220].map((x, i) => (
              <g key={i} transform={`translate(${x}, 183)`}>
                <polygon points="0,-10 9,0 0,10 -9,0" fill="#628141" opacity="0.7" />
                <polygon points="0,-6 5,0 0,6 -5,0" fill="#8BAE66" opacity="0.5" />
              </g>
            ))}

            {/* Dorsal ridge spikes */}
            {[130, 150, 170, 190, 210, 230, 250, 265].map((x, i) => (
              <polygon
                key={i}
                points={`${x},${165 - i * 0.5} ${x + 7},${180} ${x - 7},${180}`}
                fill="#5c9440"
                opacity="0.9"
              />
            ))}

            {/* Legs */}
            <line x1="155" y1="218" x2="135" y2="210" stroke="#3d6e2a" strokeWidth="8" strokeLinecap="round" />
            <line x1="135" y1="210" x2="120" y2="218" stroke="#3d6e2a" strokeWidth="7" strokeLinecap="round" />
            <line x1="120" y1="218" x2="108" y2="212" stroke="#4d8a35" strokeWidth="5" strokeLinecap="round" />

            <line x1="215" y1="218" x2="235" y2="210" stroke="#3d6e2a" strokeWidth="8" strokeLinecap="round" />
            <line x1="235" y1="210" x2="252" y2="218" stroke="#3d6e2a" strokeWidth="7" strokeLinecap="round" />
            <line x1="252" y1="218" x2="262" y2="212" stroke="#4d8a35" strokeWidth="5" strokeLinecap="round" />

            {/* Neck */}
            <ellipse cx="285" cy="183" rx="28" ry="22" fill="#4d8a35" />
            <path d="M 270 178 Q 285 165 300 178" fill="#5c9440" />

            {/* Head */}
            <ellipse cx="315" cy="170" rx="42" ry="28" fill="#4d8a35" />
            <ellipse cx="315" cy="167" rx="38" ry="24" fill="#5c9440" />

            {/* Casque / crest on head */}
            <path
              d="M 295 158 Q 305 135 320 138 Q 335 140 338 158"
              fill="#4d8a35" stroke="#3d6e2a" strokeWidth="1"
            />
            <path
              d="M 298 158 Q 307 140 320 142 Q 332 144 335 158"
              fill="#5c9440"
            />

            {/* Snout */}
            <ellipse cx="348" cy="172" rx="16" ry="10" fill="#4d8a35" />
            <ellipse cx="352" cy="171" rx="10" ry="7" fill="#5c9440" />

            {/* Nostril */}
            <circle cx="355" cy="169" r="2" fill="#2e5220" />

            {/* LEFT EYE (turret) */}
            <circle cx="308" cy="157" r="12" fill="#2e5220" />
            <circle cx="308" cy="157" r="10" fill="#1B211A" />
            <circle cx="308" cy="157" r="7" fill="#3d6e2a" />
            <g ref={eyeLeftRef} style={{ transformOrigin: "308px 157px" }}>
              <circle cx="308" cy="157" r="5" fill="#0a0f09" />
              <circle cx="310" cy="155" r="1.5" fill="#8BAE66" opacity="0.9" />
            </g>
            <circle cx="308" cy="157" r="10" fill="none" stroke="#4d8a35" strokeWidth="1.5" />

            {/* RIGHT EYE — slightly behind */}
            <circle cx="322" cy="161" r="10" fill="#2e5220" />
            <circle cx="322" cy="161" r="8" fill="#1B211A" />
            <circle cx="322" cy="161" r="5.5" fill="#3d6e2a" />
            <g ref={eyeRightRef} style={{ transformOrigin: "322px 161px" }}>
              <circle cx="322" cy="161" r="4" fill="#0a0f09" />
              <circle cx="324" cy="159" r="1.2" fill="#8BAE66" opacity="0.9" />
            </g>
            <circle cx="322" cy="161" r="8" fill="none" stroke="#4d8a35" strokeWidth="1.5" />

            {/* Tongue shooting out */}
            <path
              d="M 360 171 Q 385 168 395 165 Q 408 162 414 158"
              fill="none" stroke="#c0392b" strokeWidth="5" strokeLinecap="round"
            />
            <ellipse cx="414" cy="157" rx="6" ry="4" fill="#e74c3c" transform="rotate(-15, 414, 157)" />
            <ellipse cx="417" cy="155" rx="4" ry="3" fill="#c0392b" transform="rotate(-15, 417, 155)" />

            {/* Mouth line */}
            <path
              d="M 340 176 Q 348 179 358 175"
              fill="none" stroke="#2e5220" strokeWidth="2" strokeLinecap="round"
            />

            {/* Dewlap under chin */}
            <path d="M 295 185 Q 310 195 325 185" fill="#628141" opacity="0.6" />

            {/* Subtle shine on body */}
            <ellipse cx="185" cy="175" rx="35" ry="10" fill="#8BAE66" opacity="0.15" />
            <ellipse cx="315" cy="163" rx="14" ry="6" fill="#8BAE66" opacity="0.12" />
          </svg>
        </div>

        {/* 404 NUMBER */}
        <div className="nf-code">
          <span className="nf-four nf-four--left">4</span>
          <span className="nf-zero">0</span>
          <span className="nf-four nf-four--right">4</span>
        </div>

        {/* COPY */}
        <p className="nf-eyebrow">Lost in the jungle</p>
        <h1 className="nf-heading">Page Not Found</h1>
        <p className="nf-body">
          Even our chameleon can't find what you're looking for.<br />
          The URL might be wrong, moved, or it never existed.
        </p>

        {/* ACTIONS */}
        <div className="nf-actions">
          <button className="nf-btn nf-btn--primary" onClick={() => navigate("/")}>
            Back to Home
          </button>
          <button className="nf-btn nf-btn--ghost" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}